import { i18n } from '@lumora/common';
import React, { lazy, Suspense } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { AuthGuard, AuthProvider, ProtectedRoute } from './components/auth';
import { GlobalChatbot } from './components/chatbot/GlobalChatbot';
import {
  ErrorBoundaryWithI18n,
  LoadingSpinner,
  ToastProvider,
} from './components/ui';
import { ErrorToastListener } from './components/ui/ErrorToastListener';
import { AuthServiceProvider } from './contexts/AuthServiceContext';
import { store } from './store';

// Lazy load pages for better performance
const LandingPage = lazy(() =>
  import('./pages/Landing').then(module => ({ default: module.LandingPage }))
);
const PolicyPage = lazy(() =>
  import('./pages/Landing/components').then(module => ({
    default: module.PolicyPage,
  }))
);
const AuthPage = lazy(() =>
  import('./pages/Auth').then(module => ({ default: module.AuthPage }))
);
const EmailVerificationPage = lazy(() =>
  import('./pages/Auth').then(module => ({
    default: module.EmailVerificationPage,
  }))
);
const PasswordResetPage = lazy(() =>
  import('./pages/Auth').then(module => ({ default: module.PasswordResetPage }))
);
const OAuthCallbackPage = lazy(() =>
  import('./pages/Auth').then(module => ({ default: module.OAuthCallbackPage }))
);
const DashboardPage = lazy(() =>
  import('./pages/Dashboard').then(module => ({
    default: module.DashboardPage,
  }))
);
const AnalyticsPage = lazy(() =>
  import('./pages/Analytics').then(module => ({
    default: module.AnalyticsPage,
  }))
);
const PrivacySettingsPage = lazy(() =>
  import('./pages/Privacy').then(module => ({
    default: module.PrivacySettingsPage,
  }))
);
const OnboardingPage = lazy(() =>
  import('./pages/Onboarding').then(module => ({
    default: module.OnboardingPage,
  }))
);
const ProfilePage = lazy(() =>
  import('./pages/Profile').then(module => ({ default: module.ProfilePage }))
);
const SessionManagerPage = lazy(() =>
  import('./pages/Profile/SessionManagerPage').then(module => ({
    default: module.SessionManagerPage,
  }))
);
const GoalsPage = lazy(() =>
  import('./pages/Goals').then(module => ({ default: module.GoalsPage }))
);
const GoalDetailPage = lazy(() =>
  import('./pages/Goals').then(module => ({ default: module.GoalDetailPage }))
);
const AccountsPage = lazy(() =>
  import('./pages/Accounts').then(module => ({ default: module.AccountsPage }))
);
const AccountDetailPage = lazy(() =>
  import('./pages/Accounts').then(module => ({
    default: module.AccountDetailPage,
  }))
);
const ExplorePage = lazy(() =>
  import('./pages/Explore').then(module => ({
    default: module.ExplorePage,
  }))
);
const IdeaDetailPage = lazy(() =>
  import('./pages/Ideas').then(module => ({
    default: module.IdeaDetailPage,
  }))
);
const StockDetailPage = lazy(() =>
  import('./pages/Market').then(module => ({
    default: module.TickerDetailPage,
  }))
);
const MarketPage = lazy(() =>
  import('./pages/Market').then(module => ({
    default: module.MarketPage,
  }))
);
const CryptoPhasesPage = lazy(() =>
  import('./pages/Market').then(module => ({
    default: module.CryptoPhasesPage,
  }))
);

const App: React.FC = () => {
  return (
    <I18nextProvider i18n={i18n}>
      <ErrorBoundaryWithI18n>
        <Provider store={store}>
          <ToastProvider>
            <ErrorToastListener />
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <AuthServiceProvider>
                <AuthProvider>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      {/* Public routes */}
                      <Route
                        path="/"
                        element={
                          <AuthGuard>
                            <LandingPage />
                          </AuthGuard>
                        }
                      />
                      <Route path="/policy" element={<PolicyPage />} />

                      {/* Auth routes - allow access even when authenticated */}
                      <Route path="/auth" element={<AuthPage />} />
                      <Route
                        path="/auth/verify"
                        element={<EmailVerificationPage />}
                      />
                      <Route
                        path="/auth/reset-password"
                        element={<PasswordResetPage />}
                      />
                      <Route
                        path="/auth/callback"
                        element={<OAuthCallbackPage />}
                      />

                      {/* Protected routes */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <DashboardPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/analytics"
                        element={
                          <ProtectedRoute>
                            <AnalyticsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <ProfilePage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/profile/sessions"
                        element={
                          <ProtectedRoute>
                            <SessionManagerPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/privacy"
                        element={
                          <ProtectedRoute>
                            <PrivacySettingsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/onboarding"
                        element={
                          <ProtectedRoute>
                            <OnboardingPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/goals"
                        element={
                          <ProtectedRoute requireGoals>
                            <GoalsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/goals/:goalId"
                        element={
                          <ProtectedRoute requireGoals>
                            <GoalDetailPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/accounts"
                        element={
                          <ProtectedRoute requireGoals>
                            <AccountsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/accounts/:accountId"
                        element={
                          <ProtectedRoute requireGoals>
                            <AccountDetailPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/explore"
                        element={
                          <ProtectedRoute requireGoals>
                            <ExplorePage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/ideas/:ideaId"
                        element={
                          <ProtectedRoute requireGoals>
                            <IdeaDetailPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/ticker/:symbol"
                        element={
                          <ProtectedRoute requireGoals>
                            <StockDetailPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/market"
                        element={
                          <ProtectedRoute requireGoals>
                            <MarketPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/market/crypto-phases"
                        element={
                          <ProtectedRoute requireGoals>
                            <CryptoPhasesPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Catch all route */}
                      <Route
                        path="*"
                        element={<div>404 - Page Not Found</div>}
                      />
                    </Routes>
                  </Suspense>

                  {/* Global Chatbot - persists across all pages */}
                  <GlobalChatbot />
                </AuthProvider>
              </AuthServiceProvider>
            </Router>
          </ToastProvider>
        </Provider>
      </ErrorBoundaryWithI18n>
    </I18nextProvider>
  );
};

export default App;
